import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import {
  Table,
  type TableColumn,
  type TableSortConfig,
} from "@/components/design-system/table";
import {
  deleteExistingStudent,
  fetchStudents,
  selectNoStudents,
  selectStudentActionLoading,
  selectStudentLoading,
  selectStudents,
  selectTotalItems,
} from "@/store/slices/studentSlice";
import { AppDispatch } from "@store";
import { ChevronLeft, Eye, SquarePen, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";

// Using the StudentModel from types
import { Button } from "@/components/design-system/button";
import Modal from "@/components/design-system/Modal";
import { StudentModel, type StudentListType } from "@/types/student.model";
import {
  capitalizeFirstLetter,
  evaluationColumns,
  hasMenuPermission,
  hasRolePermissions,
  ROLE_PERMISSIONS,
} from "@/utils/constants";
import { isAllSchoolsSelected, selectSchoolId, selectUserRole } from "@/store/slices/authSlice";
import {
  fetchCsListDropdownOptions,
  type CsListGradeOption,
} from "@/service/base.service";
import SchoolSelect from "@/components/design-system/schoolSelect";
import StudentTypeSelect from "@/components/design-system/StudentTypeSelect";
import StudentTypeBadge, { resolveStudentType } from "./StudentTypeBadge";
import { toStudentListResourceParams } from "@/utils/studentListResourceParams";

// Extended interface for UI display purposes
interface StudentDisplay extends StudentModel {
  sno?: number;
}

const ITEMS_PER_PAGE = 10; // Or any other value you prefer

export default function StudentListSection() {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [sortConfig, setSortConfig] = useState<TableSortConfig | undefined>(
    undefined
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<string | null>(null);

  const [gradeOptions, setGradeOptions] = useState<CsListGradeOption[]>([]);
  const [selectedGradeId, setSelectedGradeId] = useState<string>("");
  const [selectedListType, setSelectedListType] = useState<StudentListType>("all");

  const dispatch = useDispatch<AppDispatch>();
  const students = useSelector(selectStudents);
  const actionLoading = useSelector(selectStudentActionLoading, shallowEqual);
  const loading = useSelector(selectStudentLoading, shallowEqual);
  const totalItems = useSelector(selectTotalItems, shallowEqual);
  const userRole = useSelector(selectUserRole, shallowEqual);
  const noStudents = useSelector(selectNoStudents, shallowEqual);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const schoolId = searchParams.get("schoolId");
  const schoolIdFromUser = useSelector(selectSchoolId);
  const isAllSchools = useSelector(isAllSchoolsSelected);

  // Track if filters (like grade options) are ready to fetch
  const hasFiltersReady = useRef(false);

  // Course / Class / All options from v2 dp/cs/list?type=…
  const fetchGradeOptions = async () => {
    try {
      const options = await fetchCsListDropdownOptions(selectedListType, {
        schoolId: schoolIdFromUser || undefined,
      });
      setGradeOptions(options);
      hasFiltersReady.current = true;
    } catch (error) {
      console.error("Error fetching grade options:", error);
      setGradeOptions([]);
      hasFiltersReady.current = true;
    }
  };

  useEffect(() => {
    fetchGradeOptions();
  }, [schoolIdFromUser, selectedListType]);

  useEffect(() => {
    if (gradeOptions.length > 0) {
      setSelectedGradeId(gradeOptions?.[0]?.id);
    } else {
      setSelectedGradeId("");
    }
  }, [gradeOptions]);

  // Handle grade selection change
  const handleGradeChange = (gradeIdSelected: string) => {
    setSelectedGradeId(gradeIdSelected);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleListTypeChange = (type: StudentListType) => {
    setSelectedListType(type);
    setSelectedGradeId("");
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const schoolColumn: any = {
    key: "school_name",
    title: "School Name",
    dataIndex: "school_name",
    minWidth: 250,
    maxWidth: 250,
    sortable: true,
    sortKey: "school_name",
    render: (_: any, record: any) => (
      <div
        className="text-[#1F1F1F] text-[16px] font-medium"
        style={{
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
        }}
      >
        {record?.school?.name || "N/A"}
      </div>
    ),
  };

  const columns: TableColumn<StudentDisplay>[] = [
    {
      key: "sno",
      title: "S.No",
      dataIndex: "sno",
      minWidth: 68,
      maxWidth: 68,
      align: "center",
      render: (_, __, index) => {
        // Calculate S.No based on current page and index
        const sno = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
        return (
          <div className="text-[#1F1F1F] text-[16px] font-medium text-center text-borderBlack">
            {String(sno).padStart(2, "0")}
          </div>
        );
      },
    },
    {
      key: "reg_no",
      title: "Unique ID",
      dataIndex: "reg_no",
      minWidth: 145,
      maxWidth: 145,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.reg_no}
        </div>
      ),
    },
    {
      key: "name",
      title: "Student Name",
      dataIndex: "name",
      minWidth: 250,
      maxWidth: 250,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.name}
        </div>
      ),
    },
    ...(isAllSchools ? [schoolColumn] : []),
    {
      key: "type",
      title: "Type",
      minWidth: 120,
      maxWidth: 120,
      align: "center",
      render: (_, record) => (
        <div className="flex justify-center">
          <StudentTypeBadge type={resolveStudentType(record)} />
        </div>
      ),
    },
    {
      key: "grade",
      title: "Grade",
      minWidth: 148,
      maxWidth: 148,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {Array.isArray(record.grade)
            ? record.grade
              .map((grade) =>
                typeof grade === "object" && grade !== null
                  ? grade.grade
                  : grade
              )
              .join(", ")
            : typeof record.grade === "object" && record.grade !== null
              ? record.grade.grade
              : record.grade || "N/A"}
        </div>
      ),
    },
    {
      key: "gender",
      title: "Gender",
      minWidth: 148,
      maxWidth: 148,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {capitalizeFirstLetter(record.gender) || "N/A"}
        </div>
      ),
    },
    {
      key: "dob",
      title: "Date Of Birth",
      minWidth: 148,
      maxWidth: 148,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.dob || ""}
        </div>
      ),
    },
    {
      key: "age",
      title: "Age",
      minWidth: 94,
      maxWidth: 94,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.age || ""}
        </div>
      ),
    },
    {
      key: "email",
      title: "Email Address",
      minWidth: 249,
      maxWidth: 249,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.email || "N/A"}
        </div>
      ),
    },
    {
      key: "percentage",
      title: "Course Completion %",
      minWidth: 204,
      maxWidth: 204,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {selectedGradeId ? (
            <>
              {(record.grade as any)?.find(
                (grade: { id: string }) => grade.id === selectedGradeId
              )?.completion_percent || "0%"}
            </>
          ) : (
            <>{record.course_completion || "0%"}</>
          )}
        </div>
      ),
    },
    ...evaluationColumns(selectedGradeId)
    // {
    //   key: "teacher",
    //   title: "Teacher Assigned",
    //   minWidth: 204,
    //   maxWidth: 204,
    //   render: (_, record) => (
    //     <div
    //       className="text-[#1F1F1F] text-[16px] font-medium"
    //       style={{
    //         overflowWrap: "anywhere",
    //         whiteSpace: "pre-wrap",
    //       }}
    //     >
    //       {Array.isArray(record.teachers)
    //         ? record.teachers.map((teacher) => teacher.name).join(", ")
    //         : record.teachers || "N/A"}
    //     </div>
    //   ),
    // },
  ];

  if (
    hasRolePermissions(userRole?.permissions as string[], [
      ROLE_PERMISSIONS.ACTION_VIEW_STUDENT,
      ROLE_PERMISSIONS.ACTION_EDIT_STUDENT,
      ROLE_PERMISSIONS.ACTION_DELETE_STUDENT,
    ])
  ) {
    columns.push({
      key: "action",
      title: "Action",
      minWidth: 202,
      maxWidth: 202,
      align: "left",
      render: (_, record) => (
        <div className="flex items-center justify-start space-x-2">
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_VIEW_STUDENT,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="View"
                onClick={() =>
                  navigate((schoolId || schoolIdFromUser) ? `/user-management/student/view-student/${record.id}?schoolId=${schoolId || schoolIdFromUser}` : `/user-management/student/view-student/${record.id}`)
                }
              >
                <Eye className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_EDIT_STUDENT,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Edit"
                onClick={() =>
                  navigate((schoolId || schoolIdFromUser) ? `/user-management/student/edit-student/${record.id}?schoolId=${schoolId || schoolIdFromUser}` : `/user-management/student/edit-student/${record.id}`)
                }
              >
                <SquarePen className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_DELETE_STUDENT,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Delete"
                onClick={() => {
                  setDeleteModalOpen(true);
                  setStudentToDelete(record.id);
                }}
              >
                <Trash2 className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}
        </div>
      ),
    });
  }

  // Handle delete student
  const handleDeleteStudent = () => {
    if (studentToDelete) {
      dispatch(deleteExistingStudent(studentToDelete))
        .unwrap()
        .then(() => {
          setDeleteModalOpen(false);
          setStudentToDelete(null);
        })
        .catch((error) => {
          console.log("Failed to delete Student:", error);
          // Modal stays open on error
        });
    }
  };

  // const exportOptions: ExportOption[] = [
  //   {
  //     label: "PDF",
  //     icon: FileText,
  //     action: () => console.log("Export PDF clicked"),
  //     iconColor: "text-[#5C88C5]",
  //   },
  //   {
  //     label: "Excel",
  //     icon: FileSpreadsheet,
  //     action: () => console.log("Export Excel clicked"),
  //     iconColor: "text-[#5C88C5]",
  //   },
  //   {
  //     label: "CSV",
  //     icon: Sheet,
  //     action: () => console.log("Export CSV clicked"),
  //     iconColor: "text-[#5C88C5]",
  //   },
  // ];

  // Handle date range changes from the ListHeader component
  const handleDateRangeChange = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    setFromDate(startDate);
    setToDate(endDate);
    // Reset to first page when date range changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // The page change will trigger a fetch
    }
  };

  // Handle sorting when column headers are clicked
  const handleSort = (config: TableSortConfig | null) => {
    setSortConfig(config || undefined);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Map frontend column keys to backend API sorting keys
  const getSortField = (key: string): string => {
    const sortFieldMap: Record<string, string> = {
      name: "NME",
      reg_no: "REG",
      email: "EML",
      contact_number: "CNUM",
      grade: "GRDE",
      course: "CSE",
      age: "AGE",
      is_active: "ACT",
      school_name: "SCH",
    };
    return sortFieldMap[key] || key;
  };

  // Fetch students on component mount and when search, pagination, date range, or sorting changes
  useEffect(() => {
    // Reset to first page when search value changes
    if (searchValue && currentPage !== 1) {
      setCurrentPage(1);
      return; // The page change will trigger another fetch
    }

    // Prepare sorting parameters if sort config exists
    const sortParams = sortConfig
      ? {
        sortBy: getSortField(sortConfig.key),
      }
      : {};

    const selectedOption = gradeOptions.find((o) => o.id === selectedGradeId);
    dispatch(
      fetchStudents({
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        search: searchValue,
        fromDate: fromDate,
        toDate: toDate,
        schoolId: schoolId || schoolIdFromUser || undefined,
        type: selectedListType,
        ...toStudentListResourceParams(
          selectedListType,
          selectedGradeId,
          selectedOption?.kind
        ),
        ...sortParams,
      })
    );
  }, [dispatch, currentPage, searchValue, fromDate, toDate, sortConfig, schoolIdFromUser, selectedGradeId, selectedListType]);

  const backURL: any = sessionStorage.getItem("schoolBackURL") || '/school-management';

  if (noStudents) {
    return (
      <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden">
        {(schoolId) && <button
          type="button"
          onClick={() => {
            sessionStorage.removeItem("schoolBackURL");
            navigate(backURL);
          }}
          className={`h-[40px] w-[40px] bg-[#DEEAF7] rounded-[8px] flex items-center justify-center mt-6`}
        >
          <ChevronLeft className="h-[24px] w-[24px] text-[#666666]" />
        </button>}
        <div
          className={`flex flex-col h-[calc(100vh-140px)] mb-2 overflow-x-auto justify-center items-center`}
        >
          {loading ? (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Loading...
              </p>
            </>
          ) : (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-bold mb-[9px]">
                No students added.
              </p>

              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Add student profiles and assign them to appropriate courses.
              </p>

              {hasMenuPermission(
                ROLE_PERMISSIONS.BUTTON_ADD_STUDENT,
                userRole?.permissions as string[]
              ) && (
                  <Button
                    className="w-[195px] h-[48px] bg-[#5C88C5] text-white"
                    onClick={() =>
                      navigate((schoolId || schoolIdFromUser) ? `/user-management/student/add-student?schoolId=${schoolId || schoolIdFromUser}` : "/user-management/student/add-student")
                    }
                  >
                    Add Student
                  </Button>
                )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden h-full flex flex-1 flex-col">
      <ListHeader
        title={<div className="flex items-center justify-center">
          {(schoolId) && <button
            type="button"
            onClick={() => {
              sessionStorage.removeItem("schoolBackURL");
              navigate(backURL);
            }}
            className={`h-[40px] w-[40px] bg-[#DEEAF7] rounded-[8px] flex items-center justify-center mr-2`}
          >
            <ChevronLeft className="h-[24px] w-[24px] text-[#666666]" />
          </button>}
          Student List
        </div>}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onDateRangeChange={handleDateRangeChange}
        rightAction={
          <>
            {/* Export Button */}
            {/* <ExportDropdown options={exportOptions} /> */}

            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_ADD_STUDENT,
              userRole?.permissions as string[]
            ) && (
                <Button
                  variant="primary"
                  size="md"
                  className="w-[161px] h-[48px] bg-[#5C88C5] text-white"
                  onClick={() => navigate((schoolId || schoolIdFromUser) ? `/user-management/student/add-student?schoolId=${schoolId || schoolIdFromUser}` : "/user-management/student/add-student")}
                >
                  Add Student
                </Button>
              )}
          </>
        }
        leftAction={
          <>
            <StudentTypeSelect
              value={selectedListType}
              onChange={handleListTypeChange}
              className="min-w-[200px] w-full shrink-0 sm:w-[200px]"
            />
            <div className="min-w-[200px] w-full shrink-0 sm:w-[320px]">
              <SchoolSelect
                label=""
                placeholder={
                  selectedListType === "class"
                    ? "Select Class"
                    : selectedListType === "course"
                      ? "Select Course"
                      : "Select Course or Class"
                }
                options={gradeOptions.map((g) => ({
                  value: g.id,
                  label: g.grade,
                }))}
                value={selectedGradeId}
                hideCloseIcon
                onChange={handleGradeChange}
                showAbove={false}
                maxHeight="400px"
              />
            </div>
          </>
        }
      />

      {/* Table Container with fixed header */}
      <div className="flex flex-1 flex-col border border-[#EDEDED] rounded-md overflow-x-auto">
        <div className="flex-1 overflow-auto h-full flex">
          <Table
            columns={columns}
            data={students}
            rowKey="id"
            showHeader={true}
            size="middle"
            sticky
            scroll={{ x: 1200 }}
            loading={loading}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </div>
      </div>

      <ListPagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalItems={totalItems}
        itemsPerPage={ITEMS_PER_PAGE}
        itemsLabel="Students"
      />

      {/* Delete Student Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Student"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            Are you sure want to Delete Student ?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => handleDeleteStudent()}
            disabled={actionLoading}
          >
            {actionLoading ? "Deleting..." : "Yes, Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
